import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import moment from 'moment-timezone';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { Dialog } from '../../../components/forms';
import { STAGE } from '../../../lib/jobs';

/* Vocabulary, helpers and the async confirm shared by the production sections. */

export type ProductionApi = ReturnType<typeof useProduction>;

/* same lists as v2 (cloud/modules/production/constants) */
export const EVENT_TYPES = [
    'brand_activation', 'conference', 'party', 'wedding', 'gala',
    'holiday_party', 'tour', 'photoshoot', 'pop_up', 'launch', 'headshots',
    'other',
];
export const STATUSES = [
    'pending', 'ready_for_production', 'in_production', 'show_ready',
    'past', 'wrapped', 'lost',
];
/* the server marks the activation provisional (hidden from event lists) in these */
export const PROVISIONAL = ['pending', 'lost'];
export const BOOTH_TYPES = [
    'linus', 'retrofit', 'tether_photographer', 'romr_pro', 'romr_ios',
    'event_photography', 'queso', 'other',
];
export const SOFTWARE = [
    'romr', 'clickerbooth_capture', 'clickerbooth_sharing', 'linus',
    'google_pixel_romr', 'photostat_mac', 'snappic',
];
export const BACKDROPS = [
    'custom_printed', 'client_provided', 'black_pillowcase',
    'white_pillowcase', 'grey_pillowcase', 'grey', 'na', 'wall',
    'custom_paper_roll', 'fully_custom_build',
];
export const STAFF_ROLES = ['lead', 'photographer', 'ba', 'assistant'];
export const CONTACT_ROLES = ['primary', 'billing', 'artwork', 'day_of', 'onsite', 'other'];
export const ASSIGNMENT_STATUSES = ['pending', 'confirmed', 'declined', 'cancelled'];
export const DESIGN_STATUSES = [
    'needs_artwork', 'up_next', 'waiting_on_files', 'to_do',
    'client_designing', 'doing', 'pending_internal_approval',
    'sent_to_client', 'client_changes', 'ready_for_production', 'archived',
];
export const DELIVERABLE_STATUSES = [
    'not_started', 'in_progress', 'ready_for_review', 'approved', 'delivered',
];
export const TIMEZONES = [
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
    'America/Los_Angeles', 'Pacific/Honolulu',
];

const SPECIAL: Record<string, string> = {
    ba: 'Brand ambassador',
    na: 'N/A',
    romr: 'ROMR',
    romr_pro: 'ROMR Pro',
    romr_ios: 'ROMR iOS',
    google_pixel_romr: 'Google Pixel ROMR',
    photostat_mac: 'Photostat Mac',
    day_of: 'Day-of',
};

export const label = (v?: string | null) =>
    v ? SPECIAL[v] ?? v.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '';

export const statusLabel = (s?: string | null) => (s ? STAGE[s]?.label ?? label(s) : '—');

export const fullName = (row: { firstName?: string | null; lastName?: string | null; name?: string | null; email?: string | null }) =>
    [row.firstName, row.lastName].filter(Boolean).join(' ') || row.name || row.email || 'Unnamed';

export const firstName = (row: { firstName?: string | null; name?: string | null }) =>
    row.firstName || (row.name ?? '').split(' ')[0] || 'They';

/* v2's wall-clock conversions, unchanged */
export const toLocalInput = (value?: string | null, tz?: string | null) =>
    value ? moment(value).tz(tz || 'America/New_York').format('YYYY-MM-DDTHH:mm') : '';
export const fromLocalInput = (value: string, tz?: string | null) =>
    value ? moment.tz(value, tz || 'America/New_York').toISOString() : null;
export const fmtAt = (value?: string | null, tz?: string | null) =>
    value ? moment(value).tz(tz || moment.tz.guess()).format('ddd MMM D · h:mm A') : null;

export const rateLabel = (rate?: number | string | null, rateType?: string | null) =>
    rate ? (rateType === 'flat' ? `$${+rate} flat` : `$${+rate}/hr`) : '';

export const ATTIO = 'https://app.attio.com/smilebooth';

/** HTTP status of a failed request, if any. */
export const statusOf = (e: any): number | undefined => e?.response?.status;

/** A readable reason for a failed write; raw database text is never shown. */
export function reason(e: any, fallback: string): string {
    const msg = String(e?.response?.data?.error ?? '');
    if (msg.includes('uq_staff_assignment')) return 'They’re already on the crew in that role.';
    if (statusOf(e) === 403) return 'Only Smilebooth ops admins can change production.';
    if (statusOf(e) === 404) return 'This record no longer exists — reload the page.';
    return fallback;
}

/**
 * In-flight tracking keyed per action. The ref blocks a second submit in
 * the same tick (state alone can't); the state drives disabled buttons.
 */
export function useInflight() {
    const keys = useRef(new Set<string>());
    const [busy, setBusy] = useState<Record<string, boolean>>({});
    const run = useCallback(async <T,>(key: string, fn: () => Promise<T>): Promise<{ ok: true; value: T } | { ok: false; error: any; skipped?: boolean }> => {
        if (keys.current.has(key)) return { ok: false, error: null, skipped: true };
        keys.current.add(key);
        setBusy((b) => ({ ...b, [key]: true }));
        try {
            const value = await fn();
            return { ok: true, value };
        } catch (error) {
            return { ok: false, error };
        } finally {
            keys.current.delete(key);
            setBusy((b) => {
                const next = { ...b };
                delete next[key];
                return next;
            });
        }
    }, []);
    return { busy, run };
}

/** Confirm that stays open while its request runs, and shows why it failed. */
export function AsyncConfirm({
    open,
    title,
    actionLabel,
    busyLabel,
    busy,
    error,
    danger,
    onConfirm,
    onClose,
    children,
}: {
    open: boolean;
    title: string;
    actionLabel: string;
    busyLabel: string;
    busy: boolean;
    error?: string | null;
    danger?: boolean;
    onConfirm: () => void;
    onClose: () => void;
    children: React.ReactNode;
}) {
    const close = () => {
        if (!busy) onClose();
    };
    return (
        <Dialog
            open={open}
            title={title}
            onClose={close}
            footer={
                <>
                    <button type="button" className="tb-btn" onClick={close} disabled={busy}>
                        Cancel
                    </button>
                    <button type="button" className={danger ? 'tb-new danger' : 'tb-new'} disabled={busy} onClick={onConfirm}>
                        {busy ? busyLabel : actionLabel}
                    </button>
                </>
            }
        >
            <div className="prd-confirm">{children}</div>
            {error && (
                <p className="field-e" role="alert">
                    {error}
                </p>
            )}
        </Dialog>
    );
}

/** Small icon button that removes a row. */
export function RemoveButton({ label: l, disabled, onClick }: { label: string; disabled?: boolean; onClick: () => void }) {
    return (
        <button type="button" className="ev-del prd-del" aria-label={l} title={l} disabled={disabled} onClick={onClick}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
            </svg>
        </button>
    );
}

/** A compact select for row controls (status pickers etc). */
export function RowSelect({
    value,
    options,
    disabled,
    ariaLabel,
    onChange,
    render = label,
    blank,
}: {
    value: string;
    options: string[];
    disabled?: boolean;
    ariaLabel: string;
    onChange: (v: string) => void;
    render?: (v: string) => string;
    blank?: string;
}) {
    return (
        <span className="selectwrap prd-rsel">
            <select className="input select" value={value} disabled={disabled} aria-label={ariaLabel} onChange={(e) => onChange(e.target.value)}>
                {blank !== undefined && <option value="">{blank}</option>}
                {/* keep an unknown current value visible instead of showing the first option */}
                {value && !options.includes(value) && <option value={value}>{render(value)}</option>}
                {options.map((o) => (
                    <option key={o} value={o}>
                        {render(o)}
                    </option>
                ))}
            </select>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
            </svg>
        </span>
    );
}
